import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ScrollText, UserRound, LogOut, Menu, ShieldAlert, X, Lock,
  Building2, School, Bell, Settings, Baby, Route, CalendarDays, Siren, BarChart3, Search, Mail, MapPin, Send, BookOpen, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import RoleBadge from "@/components/RoleBadge";
import GlobalSearch from "@/components/GlobalSearch";
import ShiftTracker from "@/components/ShiftTracker";
import HelpChat from "@/components/HelpChat";
import ShiftGate from "@/components/ShiftGate";
import BroadcastGate from "@/components/BroadcastGate";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/children", label: "Children", icon: Baby, perm: "children", testid: "nav-children" },
  { to: "/programmes", label: "Training Programmes", icon: Route, perm: "programmes:work", testid: "nav-programmes" },
  { to: "/schedule", label: "My Schedule", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/incidents", label: "Incidents", icon: Siren, testid: "nav-incidents" },
  { to: "/safeguarding", label: "Safeguarding", icon: Lock, testid: "nav-safeguarding" },
  { to: "/reports", label: "Reports & Analytics", icon: BarChart3, perm: "reports:executive", testid: "nav-reports" },
  { to: "/staff", label: "Staff", icon: Users, perm: "staff:read", testid: "nav-staff" },
  { to: "/councils", label: "Local Authorities", icon: Building2, perm: "councils:read", testid: "nav-councils" },
  { to: "/schools", label: "Schools", icon: School, perm: "schools:read", testid: "nav-schools" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/live-staff", label: "Live Staff", icon: MapPin, perm: "attendance:view_all", testid: "nav-live-staff" },
  { to: "/submitted-reports", label: "Submitted Reports", icon: Send, testid: "nav-submitted-reports" },
  { to: "/spot-checks", label: "Spot Checks", icon: ShieldCheck, perm: "spotchecks:read", testid: "nav-spot-checks" },
  { to: "/policies", label: "Policies & Procedures", icon: BookOpen, testid: "nav-policies" },
  { to: "/privacy", label: "Privacy", icon: ShieldCheck, testid: "nav-privacy" },
  { to: "/profile", label: "My Profile", icon: UserRound, testid: "nav-profile" },
  { to: "/settings", label: "Settings", icon: Settings, perm: "settings:manage", testid: "nav-settings" },
  { to: "/audit-logs", label: "Audit Log", icon: ScrollText, perm: "audit:read", testid: "nav-audit-logs" },
];

function SidebarContent({ onNavigate, unread }) {
  const { user, logout, hasPermission } = useAuth();
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);

  const sendContact = async () => {
    setContactSending(true);
    try {
      await api.post("/contact", { subject: contactSubject.trim(), message: contactMessage.trim() });
      toast.success("Message sent to the office");
      setContactOpen(false);
      setContactSubject("");
      setContactMessage("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setContactSending(false);
    }
  };
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 min-h-[44px] ${
      isActive ? "bg-cyan-500/20 text-white shadow-inner ring-1 ring-cyan-400/30" : "text-slate-300 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-4 py-5 border-b border-slate-700">
        <div className="flex h-10 w-16 items-center justify-center rounded-md bg-white px-1.5 py-1">
          <img src="/deluxe-logo.png" alt="Deluxe Healthcare Services" className="max-h-full max-w-full object-contain" data-testid="sidebar-logo" />
        </div>
        <div>
          <p className="font-heading text-sm font-bold text-white leading-tight">Deluxe Travel Training</p>
          <p className="text-xs text-slate-400">SEND Travel Training</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto" aria-label="Main navigation">
        {NAV_ITEMS.filter((i) => {
          if (!i.perm) return true;
          if (i.perm === "children") return hasPermission("children:read_all") || hasPermission("children:read_assigned");
          return hasPermission(i.perm);
        }).map((item) => (
          <NavLink key={item.to} to={item.to} data-testid={item.testid} className={linkClass} onClick={onNavigate}>
            <item.icon className="h-4 w-4" />
            <span className="flex-1">{item.label}</span>
            {item.to === "/notifications" && unread > 0 && (
              <span data-testid="nav-notifications-badge" className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">
                {unread}
              </span>
            )}
          </NavLink>
        ))}

      </nav>

      <div className="border-t border-slate-700 px-4 py-4">
        <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name}</p>
        <div className="mt-1 mb-3">
          <RoleBadge role={user?.role} testid="sidebar-user-role" />
        </div>
        <ShiftTracker />
        <button
          data-testid="sidebar-contact-button"
          onClick={() => setContactOpen(true)}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors duration-200 min-h-[44px]"
        >
          <Mail className="h-4 w-4" />
          Email the office
        </button>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors duration-200 min-h-[44px]"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent data-testid="contact-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email the office</DialogTitle>
            <DialogDescription>
              Your message is emailed to the Deluxe office (info@deluxehealthcare.co.uk). Replies go directly to your own email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact-subject">Subject</Label>
              <Input
                id="contact-subject"
                data-testid="contact-subject-input"
                value={contactSubject}
                onChange={(e) => setContactSubject(e.target.value)}
                maxLength={150}
                placeholder="What is this about?"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-message">Message</Label>
              <Textarea
                id="contact-message"
                data-testid="contact-message-input"
                rows={5}
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                maxLength={5000}
                placeholder="Write your message…"
              />
            </div>
            <Button
              data-testid="contact-send-button"
              onClick={sendContact}
              disabled={contactSending || contactSubject.trim().length < 2 || contactMessage.trim().length < 5}
              className="w-full min-h-[44px]"
            >
              {contactSending ? "Sending…" : "Send message"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications").then((r) => setUnread(r.data.unread_count)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div
        data-testid="confidential-banner"
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 bg-amber-600 px-3 py-1.5 text-xs font-semibold tracking-wide text-white shadow-inner"
        role="alert"
      >
        <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
        <span className="text-center">Confidential — contains sensitive SEND &amp; safeguarding data. All access is logged and audited.</span>
      </div>
      <DemoModeBanner />

      <aside className="fixed top-[34px] bottom-0 left-0 z-40 hidden w-64 bg-brand-navy lg:block">
        <SidebarContent unread={unread} />
      </aside>

      <div
        data-testid="desktop-search-bar"
        className="fixed top-[34px] left-64 right-0 z-40 hidden h-14 items-center border-b border-border bg-white px-6 lg:flex"
      >
        <GlobalSearch />
      </div>

      <header className="fixed top-[34px] left-0 right-0 z-40 flex items-center gap-3 border-b border-border bg-white px-4 py-3 lg:hidden">
        <button
          data-testid="mobile-menu-button"
          onClick={() => setMobileOpen(true)}
          className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-muted transition-colors duration-200"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/deluxe-logo.png" alt="Deluxe Healthcare Services" className="h-7 w-auto" data-testid="mobile-header-logo" />
          <span className="font-heading text-sm font-bold">Deluxe Travel Training</span>
        </div>
        <button
          data-testid="mobile-search-button"
          onClick={() => setSearchOpen((v) => !v)}
          className="ml-auto flex h-11 w-11 items-center justify-center rounded-md hover:bg-muted transition-colors duration-200"
          aria-label="Search help and records"
          aria-expanded={searchOpen}
        >
          <Search className="h-5 w-5" />
        </button>
      </header>

      {searchOpen && (
        <div
          data-testid="mobile-search-bar"
          className="fixed top-[98px] left-0 right-0 z-40 border-b border-border bg-white px-4 py-2 lg:hidden"
        >
          <GlobalSearch autoFocus onNavigate={() => setSearchOpen(false)} />
        </div>
      )}

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-brand-navy">
            <button
              data-testid="mobile-menu-close-button"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-md text-slate-300 hover:bg-white/10"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent unread={unread} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <main className="pt-[98px] lg:pl-64 lg:pt-[90px]">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          <Outlet />
        </div>
      <HelpChat />
      <ShiftGate />
      <BroadcastGate />
      </main>
    </div>
  );
}

function DemoModeBanner() {
  const [demo, setDemo] = useState(false);
  useEffect(() => {
    api.get("/settings/mode").then((r) => setDemo(r.data.app_mode === "demo")).catch(() => {});
  }, []);
  if (!demo) return null;
  return (
    <div
      data-testid="demo-mode-banner"
      className="fixed bottom-0 left-0 right-0 z-50 bg-red-600 px-3 py-1.5 text-center text-xs font-bold tracking-widest text-white shadow-lg"
    >
      DEMO / TRAINING MODE — NOT REAL DATA
    </div>
  );
}
